import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, Trophy, Award, Medal, Shirt, Gift, Clock, Truck, Sparkles, ArrowRight, ArrowUpRight, Phone, MapPin, Mail, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import QuoteWidget from '../components/QuoteWidget';

const Homepage = () => {
  const { t } = useTranslation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Real RC Awards shop images
  const shopImages = {
    hero: 'https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/qafcxzfa_unnamed.webp', // Shop interior wide shot
    trophies: 'https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/oumok1jw_unnamed%20%286%29.webp', // Trophies display
    crystal: 'https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/ehppwmya_unnamed%20%287%29.webp', // Crystal/art awards
    sportsTrophies: 'https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/f08oactp_unnamed%20%288%29.webp', // Sports trophies wall
    storefront: 'https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/8lcfsx9e_unnamed%20%289%29.webp', // Storefront exterior
  };
  
  const products = [
    {
      id: 'trophies',
      name: 'Custom Trophies',
      image: shopImages.trophies,
      description: 'Sports trophies, corporate awards, Little League, Major League, and fantasy sports prizes',
      features: ['Sports Awards', 'Corporate Awards', 'Fantasy League'],
      startingPrice: 15,
      popular: true
    },
    {
      id: 'crystal',
      name: 'Crystal & Art Glass Awards',
      image: shopImages.crystal,
      description: 'Elegant crystal and art glass awards for executive recognition and milestone celebrations',
      features: ['3D Laser Engraving', 'Art Glass', 'Premium Gift Boxes'],
      startingPrice: 45,
      popular: false
    },
    {
      id: 'sports',
      name: 'Sports Trophies & Medals',
      image: shopImages.sportsTrophies,
      description: 'Complete selection of sports trophies for baseball, soccer, basketball, and all athletic events',
      features: ['All Sports', 'Team Discounts', 'Rush Available'],
      startingPrice: 10,
      popular: false
    }
  ];

  // Real Google Reviews from RC Awards
  const reviews = [
    {
      name: 'Krisiana K.',
      avatar: 'https://lh3.googleusercontent.com/a/default-user=s40-c',
      rating: 5,
      comment: 'Amazing team and very helpful! Got the job done quickly and efficiently! I had a photo frame engraved and it came out beautiful! Will definitely be back here!!!',
      date: '2 weeks ago'
    },
    {
      name: 'Mathy S.',
      avatar: 'https://lh3.googleusercontent.com/a/default-user=s40-c',
      rating: 5,
      comment: 'Abe and Jonathan were GREAT!! They did miracle work!! I needed a last minute engraving and they were able to make it with a smile on their faces and amazing attitude!!! Extremely helpful!!!!',
      date: '3 weeks ago'
    },
    {
      name: 'Nico D.',
      avatar: 'https://lh3.googleusercontent.com/a/default-user=s40-c',
      rating: 5,
      comment: 'Better late than never! YONATHAN IS THE BEST!! I had two back-to-back orders and he came through with amazing work that is only matched by his professionalism and top-tier customer service!',
      date: '1 month ago'
    },
    {
      name: 'Thomas B.',
      avatar: 'https://lh3.googleusercontent.com/a/default-user=s40-c',
      rating: 5,
      comment: 'Amazing guy! Good business man very friendly good positive attitude and very knowledgeable about the world',
      date: '1 month ago'
    },
    {
      name: 'Jessica L.',
      avatar: 'https://lh3.googleusercontent.com/a/default-user=s40-c',
      rating: 5,
      comment: 'Absolutely amazing work. I needed a last minute plaque and every other store blew me off. Not RC Awards. They were thorough, communicative, and the plaque was PERFECT! HIGHLY recommend!!!',
      date: '2 months ago'
    },
    {
      name: 'Jason N.',
      avatar: 'https://lh3.googleusercontent.com/a/default-user=s40-c',
      rating: 5,
      comment: 'Exceptional service and high quality trophies! I had a rushed order for a corporate event, and Yonathan & crew went above and beyond to ensure the order was precisely accurate and on time.',
      date: '2 months ago'
    }
  ];

  const services = [
    { icon: Trophy, name: 'Trophies' },
    { icon: Award, name: 'Plaques' },
    { icon: Medal, name: 'Medals & Pins' },
    { icon: Sparkles, name: 'Crystal Awards' },
    { icon: Shirt, name: 'Custom Apparel' },
    { icon: Gift, name: 'Promotional Products' }
  ];

  const stats = [
    { value: '5.0', label: 'Google Rating (200+ Reviews)' },
    { value: '1995', label: 'Serving San Diego Since' },
    { value: '4 Days', label: 'Standard Turnaround' }
  ];

  const whyChooseUs = [
    { icon: Star, title: 'Best Prices in San Diego', desc: 'Competitive pricing guaranteed' },
    { icon: Clock, title: '4-Day Turnaround', desc: 'Fast standard production' },
    { icon: Sparkles, title: 'Rush Services', desc: 'Same-day & next-day available' },
    { icon: Truck, title: 'Free Local Delivery', desc: 'San Diego metro area' }
  ];

  const programTypes = [
    'Corporate Recognition Programs',
    'Employee Service Awards',
    'Safety Programs',
    'Sports Awards & Tournaments',
    'School Awards',
    'Little League & Youth Sports',
    'Fantasy Sports Leagues',
    'Business Gifts',
    'Product Introductions',
    'Company Outings',
    'Thank You Programs',
    'E-Commerce & Fulfillment'
  ];

  return (
    <div data-testid="homepage" className="min-h-screen bg-white">
      {/* ===== HERO SECTION - SEO OPTIMIZED ===== */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={shopImages.hero}
            alt="RC Awards Trophy Shop Interior - San Diego's Premier Awards Store"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a365d]/95 via-[#1a365d]/85 to-[#1a365d]/60"></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 w-full">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-3 mb-6"
            >
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} className="fill-[#D4AF37] text-[#D4AF37]" />
                ))}
              </div>
              <span className="text-sm text-white/90 font-medium bg-white/15 backdrop-blur-md px-4 py-1.5 rounded-full">
                San Diego's #1 Rated Trophy Shop Since 1995
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              data-testid="hero-heading"
              className="text-4xl sm:text-5xl lg:text-6xl text-white mb-6 leading-[1.1] font-bold"
            >
              Custom Trophies, Plaques & Awards in San Diego, CA
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-base sm:text-lg text-white/80 mb-8 max-w-2xl leading-relaxed"
            >
              Full-service awards and promotional products company serving San Diego County. Corporate recognition, sports awards, employee service awards, and promotional items — all with fast 4-day turnaround and the best prices guaranteed.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-3 mb-8"
            >
              <a
                href="https://www.rcawards.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-shop-btn"
                className="bg-[#D4AF37] text-[#1a365d] px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-[#c9a432] active:scale-[0.97] transition-all duration-300 flex items-center justify-center gap-2 shadow-lg"
              >
                Shop Online
                <ArrowUpRight size={16} />
              </a>
              <Link
                to="/contact"
                data-testid="hero-quote-btn"
                className="bg-white/10 backdrop-blur-sm border border-white/30 text-white px-7 py-3.5 rounded-full text-sm font-medium hover:bg-white/20 active:scale-[0.97] transition-all duration-300 flex items-center justify-center gap-2"
              >
                Get Free Quote
                <ArrowRight size={16} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex flex-wrap items-center gap-4 pt-6 border-t border-white/20"
            >
              <a href="tel:6196401633" className="flex items-center gap-2 text-white hover:text-[#D4AF37] transition-colors bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Phone size={16} />
                <span className="text-sm font-medium">(619) 640-1633</span>
              </a>
              <a href="sms:6196401633" className="flex items-center gap-2 text-white hover:text-[#D4AF37] transition-colors bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
                <span className="text-sm font-medium">Text Us</span>
              </a>
              <a href="mailto:RCAwards@Aol.com" className="flex items-center gap-2 text-white/80 hover:text-[#D4AF37] transition-colors bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Mail size={16} />
                <span className="text-sm">Email</span>
              </a>
              <div className="flex items-center gap-2 text-white/80 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <MapPin size={16} />
                <span className="text-sm">San Diego, CA</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== SEO SERVICE AREAS ===== */}
      <section className="bg-[#D4AF37] py-4">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[#1a365d] text-sm font-medium">
            <span>Serving:</span>
            <span>San Diego</span>
            <span>•</span>
            <span>La Mesa</span>
            <span>•</span>
            <span>El Cajon</span>
            <span>•</span>
            <span>Chula Vista</span>
            <span>•</span>
            <span>National City</span>
            <span>•</span>
            <span>Coronado</span>
            <span>•</span>
            <span>All SD County</span>
          </div>
        </div>
      </section>

      {/* ===== ABOUT / STATS ===== */}
      <section data-testid="about-section" className="py-24 lg:py-32 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-3">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="text-xs uppercase tracking-widest text-[#D4AF37] mb-4 font-semibold"
              >
                San Diego's Premier Awards Shop
              </motion.p>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="text-3xl sm:text-4xl lg:text-[2.75rem] text-[#1a365d] mb-6 leading-[1.15] font-bold"
              >
                Full-Service Advertising Specialties & Custom Awards Company
              </motion.h2>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-base text-gray-600 leading-relaxed space-y-4"
              >
                <p>
                  RC Awards is a full-service advertising specialties company offering a wide range of promotional items to promote your business. Located in San Diego since 1995, we help you find cost-effective ways to market and promote your brand with custom promotional items that speak to your customers and clients.
                </p>
                <p>
                  With the number of companies we work with, the possibilities are only limited by your imagination. From corporate recognition programs to youth sports leagues, we deliver quality products at the best prices in San Diego.
                </p>
              </motion.div>
            </div>

            <div className="lg:col-span-2 space-y-8 lg:pt-12">
              {stats.map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="text-right"
                >
                  <p className="text-4xl lg:text-5xl text-[#1a365d] font-bold">{stat.value}</p>
                  <p className="text-sm text-gray-500 mt-1">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== SHOP GALLERY SECTION ===== */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-xs uppercase tracking-widest text-[#D4AF37] mb-3 font-semibold">Visit Our Showroom</p>
            <h2 className="text-3xl sm:text-4xl text-[#1a365d] mb-4 font-bold">
              See Our Award Selection in Person
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Stop by our San Diego showroom to browse thousands of trophies, plaques, crystal awards, and promotional products.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
              className="col-span-2 row-span-2 rounded-2xl overflow-hidden shadow-lg"
            >
              <img src={shopImages.hero} alt="RC Awards Shop Interior" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              viewport={{ once: true }}
              className="rounded-2xl overflow-hidden shadow-lg"
            >
              <img src={shopImages.trophies} alt="Trophy Display" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              viewport={{ once: true }}
              className="rounded-2xl overflow-hidden shadow-lg"
            >
              <img src={shopImages.crystal} alt="Crystal Awards" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.3 }}
              viewport={{ once: true }}
              className="rounded-2xl overflow-hidden shadow-lg"
            >
              <img src={shopImages.sportsTrophies} alt="Sports Trophies" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.4 }}
              viewport={{ once: true }}
              className="rounded-2xl overflow-hidden shadow-lg"
            >
              <img src={shopImages.storefront} alt="RC Awards Storefront" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </motion.div>
          </div>

          <div className="text-center mt-8">
            <Link
              to="/gallery"
              className="inline-flex items-center gap-1.5 text-sm text-[#1a365d] font-medium hover:gap-2.5 transition-all duration-300"
            >
              View Full Gallery
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== WHAT WE OFFER ===== */}
      <section data-testid="programs-section" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-xs uppercase tracking-widest text-[#D4AF37] mb-3 font-semibold">Our Programs</p>
            <h2 className="text-3xl sm:text-4xl text-[#1a365d] mb-4 font-bold">
              Custom Recognition Solutions for Every Need
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              We assist your marketing objectives with quality recognition programs tailored to your specific needs.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-10">
            {programTypes.map((program, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.03 }}
                viewport={{ once: true }}
                className="flex items-center gap-2 bg-gray-50 rounded-lg px-4 py-3 border border-gray-100 hover:border-[#D4AF37]/30 hover:shadow-sm transition-all duration-300"
              >
                <CheckCircle size={16} className="text-[#D4AF37] flex-shrink-0" />
                <span className="text-sm text-gray-700">{program}</span>
              </motion.div>
            ))}
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  data-testid={`service-${index}`}
                  className="flex items-center gap-2 px-5 py-3 bg-white rounded-full border border-gray-200 text-sm text-gray-700 hover:border-[#1a365d]/30 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
                >
                  <Icon size={16} className="text-[#D4AF37]" />
                  {service.name}
                </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-8">
            <a
              href="https://www.rcawards.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="view-all-products-btn"
              className="inline-flex items-center gap-1.5 text-sm text-[#1a365d] font-medium hover:gap-2.5 transition-all duration-300"
            >
              Browse All Products Online
              <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* ===== FEATURED PRODUCTS ===== */}
      <section data-testid="products-section" className="py-24 lg:py-32 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <p className="text-xs uppercase tracking-widest text-[#D4AF37] mb-3 font-semibold">Featured Products</p>
            <h2 className="text-3xl sm:text-4xl text-[#1a365d] mb-4 font-bold">
              Popular Award Categories
            </h2>
            <p className="text-base text-gray-600 max-w-lg mx-auto">
              Browse our most popular recognition products or visit our online store for the full selection.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl hover:border-gray-200 transition-all duration-500"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={product.image}
                    alt={`${product.name} - San Diego Custom Awards`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  {product.popular && (
                    <span className="absolute top-4 left-4 bg-[#D4AF37] text-[#1a365d] text-xs font-semibold px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-[#1a365d] mb-2">{product.name}</h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{product.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {product.features.map((feature, i) => (
                      <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                        {feature}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <span className="text-sm text-gray-500">Starting at <span className="text-[#1a365d] font-semibold">${product.startingPrice}</span></span>
                    <a
                      href="https://www.rcawards.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-[#D4AF37] font-medium hover:text-[#c9a432] transition-colors flex items-center gap-1"
                    >
                      Shop <ArrowUpRight size={14} />
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-12">
            <a
              href="https://www.rcawards.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="shop-all-btn"
              className="bg-[#1a365d] text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-[#152a4a] hover:shadow-lg active:scale-[0.97] transition-all duration-300 inline-flex items-center gap-2"
            >
              Shop All Products
              <ArrowUpRight size={14} />
            </a>
            <Link
              to="/contact"
              className="border border-gray-200 text-gray-600 px-6 py-3 rounded-full text-sm font-medium hover:border-gray-300 hover:bg-white active:scale-[0.97] transition-all duration-300 inline-flex items-center gap-2"
            >
              Request Custom Quote
            </Link>
          </div>
        </div>
      </section>

      {/* ===== WHY CHOOSE US ===== */}
      <section data-testid="why-us-section" className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-[#1a365d]"></div>
        <div className="relative z-10 max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-xs uppercase tracking-widest text-[#D4AF37] mb-4 font-semibold">Why San Diego Chooses RC Awards</p>
            <h2 className="text-3xl sm:text-4xl text-white mb-4 font-bold">
              Quality, Speed & Value — Guaranteed
            </h2>
            <p className="text-base text-white/70 max-w-xl mx-auto">
              4-day standard turnaround, rush services available, and the best prices in San Diego County.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/10"
                >
                  <div className="w-14 h-14 bg-[#D4AF37] rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon size={24} className="text-[#1a365d]" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-white/60">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/contact"
              data-testid="explore-products-btn"
              className="bg-[#D4AF37] text-[#1a365d] px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-[#c9a432] active:scale-[0.97] transition-all duration-300 inline-flex items-center gap-2"
            >
              Get Your Free Quote Today
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== GOOGLE REVIEWS ===== */}
      <section data-testid="reviews-section" className="py-24 lg:py-32 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 mb-16">
            <div>
              <p className="text-xs uppercase tracking-widest text-[#D4AF37] mb-3 font-semibold">Real Google Reviews</p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-5xl sm:text-6xl text-[#1a365d] font-bold">5.0</h2>
                  <div className="flex flex-col">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={20} className="fill-[#D4AF37] text-[#D4AF37]" />
                      ))}
                    </div>
                    <span className="text-sm text-gray-500">Google Rating</span>
                  </div>
                </div>
                <p className="text-sm text-gray-500">
                  Based on 200+ verified Google reviews
                </p>
              </motion.div>
            </div>
            <div className="flex gap-3">
              <a
                href="https://g.page/r/CXB-cWhaGOGqEAI/review"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#D4AF37] text-[#1a365d] px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#c9a432] transition-colors inline-flex items-center gap-1.5"
              >
                Write a Review
              </a>
              <Link
                to="/reviews"
                data-testid="view-all-reviews-btn"
                className="text-sm text-[#1a365d] hover:text-[#D4AF37] flex items-center gap-1.5 hover:gap-2.5 transition-all duration-300 font-medium border border-gray-200 px-5 py-2.5 rounded-full"
              >
                View All <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-2xl p-6 border border-gray-100"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#1a365d] rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      {review.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-[#1a365d]">{review.name}</p>
                      <p className="text-xs text-gray-400">{review.date}</p>
                    </div>
                  </div>
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-blue-500" fill="currentColor">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>
                <div className="flex gap-0.5 mb-3">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} size={14} className="fill-[#D4AF37] text-[#D4AF37]" />
                  ))}
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{review.comment}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== LOCATION ===== */}
      <section data-testid="location-section" className="py-24 lg:py-32 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <p className="text-xs uppercase tracking-widest text-[#D4AF37] mb-3 font-semibold">Visit Our San Diego Shop</p>
              <h2 className="text-3xl sm:text-4xl text-[#1a365d] mb-6 font-bold">
                Located on El Cajon Blvd — Easy Access from All of San Diego
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Stop by our showroom to see our products in person. Our knowledgeable team is ready to help you find the perfect recognition solution for your needs. Free parking available.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <MapPin size={20} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-[#1a365d]">Address</p>
                    <p className="text-gray-600">2509 El Cajon Blvd, San Diego, CA 92104</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone size={20} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-[#1a365d]">Phone</p>
                    <a href="tel:6196401633" className="text-gray-600 hover:text-[#D4AF37] transition-colors">(619) 640-1633</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail size={20} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-[#1a365d]">Email</p>
                    <a href="mailto:RCAwards@Aol.com" className="text-gray-600 hover:text-[#D4AF37] transition-colors">RCAwards@Aol.com</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock size={20} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-[#1a365d]">Hours</p>
                    <p className="text-gray-600">Mon-Fri: 9am-6pm | Sat: 10am-1pm</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://maps.google.com/maps?q=2509+El+Cajon+Blvd,+San+Diego,+CA+92104"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#1a365d] text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-[#152a4a] transition-colors inline-flex items-center justify-center gap-2"
                >
                  Get Directions
                  <ArrowUpRight size={14} />
                </a>
                <a
                  href="tel:6196401633"
                  className="border border-gray-200 text-gray-600 px-6 py-3 rounded-full text-sm font-medium hover:border-gray-300 hover:bg-white transition-colors inline-flex items-center justify-center gap-2"
                >
                  <Phone size={14} />
                  Call Now
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img src={shopImages.storefront} alt="RC Awards Storefront - 2509 El Cajon Blvd" className="w-full h-[250px] object-cover" />
              </div>
              <div className="rounded-2xl overflow-hidden h-[200px] shadow-xl">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3356.789!2d-117.13735!3d32.7548!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80d954d3a5d9b36d%3A0x2ae1885968717e70!2sRC%20Awards!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="RC Awards Location - San Diego Trophy Shop"
                ></iframe>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section data-testid="final-cta-section" className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a365d] to-[#0f2544]"></div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={16} className="fill-[#D4AF37] text-[#D4AF37]" />
              ))}
            </div>
            <span className="text-sm text-white/60">Join thousands of satisfied San Diego customers</span>
          </div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-3xl sm:text-4xl lg:text-5xl text-white mb-5 font-bold"
          >
            Ready to Create Something Special?
          </motion.h2>
          <p className="text-base text-white/60 mb-8 max-w-lg mx-auto leading-relaxed">
            Tell us about your project — we'll provide a free quote and help you choose the perfect recognition solution for your San Diego business or organization.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/contact"
              data-testid="final-cta-quote-btn"
              className="bg-[#D4AF37] text-[#1a365d] px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-[#c9a432] active:scale-[0.97] transition-all duration-300 inline-flex items-center justify-center gap-2"
            >
              Get Your Free Quote
              <ArrowRight size={14} />
            </Link>
            <a
              href="https://www.rcawards.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="border border-white/30 text-white px-7 py-3.5 rounded-full text-sm font-medium hover:bg-white/10 active:scale-[0.97] transition-all duration-300 inline-flex items-center justify-center gap-2"
            >
              Shop Online Now
              <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </section>

      <QuoteWidget variant="sticky" />
    </div>
  );
};

export default Homepage;
